import { Injectable } from '@angular/core';
import { UtilityService } from './utility.service';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import Resources from '@constants/resourses';

interface SupplierConfigResponse {
  data?: {
    Generic?: {
      topSpotlightVariant?: {
        configValue?: number;
        overrideCookie?: boolean;
        variant?: 'A' | 'B';
      };
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class ABTestingService {
  constructor(private utilityService: UtilityService) {}

  /**
   * Get the top picks (spotlight) variant from cookie or random assignment based on supplier configuration
   * @returns Observable<string> that emits 'A' or 'B' indicating which variant should be used
   */
  getTopSpotlightVariant(): Observable<string> {
    // Get the stored variant from cookies first
    const storedVariant = this.utilityService.getCookie(Resources.ABtestingKeys.TOP_SPOTLIGHT_VARIANT_COOKIE);

    // Always make the API call to check for overrideCookie property
    return this.utilityService.getDefaultSupplierConfiguration().pipe(
      map((response: SupplierConfigResponse) => {
        let variant = 'A'; // Default to 'A'

        if (response?.data?.Generic?.topSpotlightVariant) {
          const config = response.data.Generic.topSpotlightVariant;
          const configValue = config.configValue || 0;
          const overrideCookie = config.overrideCookie || false;
          const topSpotlightVariantValue = config.variant || 'A';

          // Check if we should use cookie value or generate new one
          if (storedVariant !== null && !overrideCookie) {
            // Use stored variant when cookie exists and overrideCookie is false
            return storedVariant;
          }

          // Generate new variant based on API configuration
          // configValue represents the percentage for the specified variant
          // If variant is 'B' and configValue is 40, then 40% get 'B' and 60% get 'A'
          const randomValue = Math.random() * 100;

          if (randomValue < configValue) {
            variant = topSpotlightVariantValue;
          } else {
            variant = topSpotlightVariantValue === 'A' ? 'B' : 'A';
          }
        } else if (storedVariant !== null) {
          // No config available but cookie exists, use cookie value
          return storedVariant;
        }

        // Set the cookie with the calculated variant
        this.utilityService.setCookie(Resources.ABtestingKeys.TOP_SPOTLIGHT_VARIANT_COOKIE, variant, 30 * 24); // 30 days expiry
        return variant;
      }),
      catchError(() => {
        // In case of error, check if we have a stored variant to fall back to
        if (storedVariant !== null) {
          return of(storedVariant);
        }

        // No stored variant and API error, default to 'A'
        const defaultVariant = 'A';

        this.utilityService.setCookie(Resources.ABtestingKeys.TOP_SPOTLIGHT_VARIANT_COOKIE, defaultVariant, 30 * 24); // 30 days expiry
        return of(defaultVariant);
      })
    );
  }
}
